import { useEffect, useState, useCallback, useContext, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { CSVLink } from "react-csv";
import { useNavigate } from "react-router-dom";
import Moment from "moment";
import AuthContext from "../../../auth/AuthContext";
import AlertService from "../../../services/AlertService";
import UserService from "../../../services/UserService";
import { Skeleton } from "primereact/skeleton";

const RecentLoginHistoryReport = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const headers = [
    { label: "Display Name", key: "displayName" },
    { label: "Email", key: "email" },
    { label: "Courses", key: "courses" },
    { label: "Login Time", key: "loginTime" },
    { label: "Ip Address", key: "ipAddress" },
    { label: "User Agent", key: "userAgent" },
    { label: "Country", key: "country" },
    { label: "Country Code", key: "countryCode" },
    { label: "Region", key: "region" },
    { label: "Region Name", key: "regionName" },
    { label: "City", key: "city" },
    { label: "Zip", key: "zip" },
    { label: "Latitude", key: "latitude" },
    { label: "Longitude", key: "longitude" },
    { label: "Time Zone", key: "timeZone" },
    { label: "ISP", key: "isp" },
    { label: "Organization", key: "organization" },
    { label: "Session Id", key: "sessionId" },
  ];

  const supportAccess = authContext.getPermissions("report");

  useEffect(() => {
    if (
      supportAccess.filter((f) => f.access.includes(1)).length === 0 ||
      authContext.onGoingValidations
    ) {
      navigate("/");
    }
    // eslint-disable-next-line
  }, [supportAccess, authContext]);

const loadResults = useCallback(async (signal) => {
    setLoading(true);
    try {
      const data = await UserService.GetLoginHistory(authContext, signal);

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setResults(data.data);
        setCurrentPage(0);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [authContext]);

  useEffect(() => {
    const controller = new AbortController();
    loadResults(controller.signal);
    return () => controller.abort();
  }, [loadResults]);

  const loginTimeBodyTemplate = (rowData) => {
    if (!rowData.loginTime) return null;
    return Moment(new Date(rowData.loginTime + "Z")).format(
      "DD/MM/YYYY hh:mm A"
    );
  };

  const sortedResults = useMemo(() => {
    let sorted = [...results];
    if (sortField) {
      sorted.sort((a, b) => {
        const valA = a[sortField];
        const valB = b[sortField];
        if (valA == null) return -1;
        if (valB == null) return 1;
        return (
          (valA < valB ? -1 : valA > valB ? 1 : 0) * (sortOrder === 1 ? 1 : -1)
        );
      });
    }
    return sorted;
  }, [results, sortField, sortOrder]);

  const paginatedClients = useMemo(() => {
    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedResults.slice(startIndex, endIndex);
  }, [sortedResults, currentPage, rowsPerPage]);

  const generatePageNumbers = () => {
    const totalPages = Math.ceil(sortedResults.length / rowsPerPage);
    const currentPageNumber = currentPage + 1;
    const pages = [];
    const maxVisiblePages = 3;

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPageNumber > maxVisiblePages + 1) pages.push("...");
      let start = Math.max(2, currentPageNumber - 1);
      let end = Math.min(totalPages - 1, currentPageNumber + 1);
      if (currentPageNumber <= maxVisiblePages) end = 4;
      else if (currentPageNumber > totalPages - maxVisiblePages)
        start = totalPages - 3;
      for (let i = start; i <= end; i++) {
        if (i > 1 && i < totalPages) pages.push(i);
      }
      if (currentPageNumber < totalPages - maxVisiblePages) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  // SKELETON UI
  const skeletonData = Array.from({ length: rowsPerPage }, (_, i) => ({
    id: `skeleton-${i}`,
  }));
  const skeletonBody = () => <Skeleton width="90%" height="1.5rem" />;

  const renderValue = (value) => {
    return value === null || value === undefined || value === "" ? "-" : value;
  };

  return (
    <div className="container-fluid custom-container position-relative mb-5">
      <div className="d-flex flex-column flex-md-row justify-content-end align-items-start align-items-md-center mb-3">
        <div className="d-flex flex-wrap gap-4 pt-3">
          <CSVLink
            data={results}
            headers={headers}
            filename={"Recent-Logins-Report.csv"}
            className="btn btn-primary"
          >
            <i className="pi pi-upload me-2"></i>Export
          </CSVLink>
        </div>
      </div>

      <DataTable
        value={loading ? skeletonData : paginatedClients}
        loading={false}
        emptyMessage={loading ? "" : "No Data Found"}
        dataKey="id"
        className="custom-table"
        sortField={sortField}
        sortOrder={sortOrder}
        responsiveLayout="scroll"
        onSort={(e) => {
          setSortField(e.sortField);
          setSortOrder(e.sortOrder);
        }}
      >
        <Column
          header="S. No."
          body={
            loading
              ? skeletonBody
              : (_rowData, { rowIndex }) =>
                  currentPage * rowsPerPage + rowIndex + 1
          }
          style={{ width: "80px", textAlign: "center" }}
        />
        <Column
          field="displayName"
          header="Display Name"
          body={
            loading
              ? skeletonBody
              : (rowData) => renderValue(rowData.displayName)
          }
        />

        <Column
          field="email"
          header="Email"
          body={
            loading ? skeletonBody : (rowData) => renderValue(rowData.email)
          }
        />

        <Column
          field="courses"
          header="Courses"
          body={
            loading ? skeletonBody : (rowData) => renderValue(rowData.courses)
          }
        />

        <Column
          field="loginTime"
          header="Login Time"
          body={loading ? skeletonBody : loginTimeBodyTemplate}
        />

        <Column
          field="ipAddress"
          header="IP Address"
          body={
            loading ? skeletonBody : (rowData) => renderValue(rowData.ipAddress)
          }
        />

        <Column
          field="userAgent"
          header="User Agent"
          body={
            loading ? skeletonBody : (rowData) => renderValue(rowData.userAgent)
          }
        />

        <Column
          field="country"
          header="Country"
          body={
            loading ? skeletonBody : (rowData) => renderValue(rowData.country)
          }
        />

        <Column
          field="regionName"
          header="Region Name"
          body={
            loading
              ? skeletonBody
              : (rowData) => renderValue(rowData.regionName)
          }
        />

        <Column
          field="city"
          header="City"
          body={loading ? skeletonBody : (rowData) => renderValue(rowData.city)}
        />

        <Column
          field="zip"
          header="Zip"
          body={loading ? skeletonBody : (rowData) => renderValue(rowData.zip)}
        />

        <Column
          field="latitude"
          header="Latitude"
          body={
            loading ? skeletonBody : (rowData) => renderValue(rowData.latitude)
          }
        />

        <Column
          field="longitude"
          header="Longitude"
          body={
            loading ? skeletonBody : (rowData) => renderValue(rowData.longitude)
          }
        />
      </DataTable>

      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-center mt-3 gap-2 paginator-wrapper">
        <div className="d-flex align-items-center">
          <label className="me-2 fw-bold">Show Rows:</label>
          {loading ? (
            <Skeleton width="100px" height="38px" />
          ) : (
            <select
              className="form-select form-select-sm w-auto py-2 text-secondary custom-input-color"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(parseInt(e.target.value));
                setCurrentPage(0);
              }}
            >
              {[5, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          )}
        </div>
        {loading ? (
          <div className="d-flex gap-2">
            <Skeleton width="40px" height="40px" />
            <Skeleton width="40px" height="40px" />
            <Skeleton width="40px" height="40px" />
            <Skeleton width="40px" height="40px" />
            <Skeleton width="40px" height="40px" />
          </div>
        ) : (
          <div className="d-flex justify-content-center align-items-center gap-2">
            <button
              className="btn p-0 fs-5 pagination-item"
              style={{ width: "40px", height: "40px" }}
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
            >
              &lt;
            </button>
            {generatePageNumbers().map((page, index) =>
              page === "..." ? (
                <span key={index} className="mx-1 text-secondary">
                  ...
                </span>
              ) : (
                <button
                  key={index}
                  className={`btn p-0 pagination-item ${
                    currentPage + 1 === page ? "active-page" : ""
                  }`}
                  style={{ width: "40px", height: "40px" }}
                  onClick={() => setCurrentPage(page - 1)}
                >
                  {page}
                </button>
              )
            )}
            <button
              className="btn p-0 fs-5 pagination-item"
              style={{ width: "40px", height: "40px" }}
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(
                    Math.ceil(sortedResults.length / rowsPerPage) - 1,
                    prev + 1
                  )
                )
              }
              disabled={
                currentPage >= Math.ceil(sortedResults.length / rowsPerPage) - 1
              }
            >
              &gt;
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentLoginHistoryReport;
